import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UsersThree, Package, ArrowRight } from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { apiPath } from '../../../config/apiBase';
import {
  authHeaders,
  btnSecondary,
  thStyle,
  tdStyle,
  ticketClientBranchLabel,
} from '../api/ticketsApi';
import TicketStatusBadge from '../components/TicketStatusBadge';
import { TicketDetailLink } from '../ticketNav';

const PRODUCTS_API = apiPath('/api/products');

function ManagerTeamPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${PRODUCTS_API}/manager/teams`, authHeaders());
      const rows = res.data || [];
      setTeams(rows);
      setSelectedId((prev) => {
        if (prev && rows.some((t) => t.product_id === prev)) return prev;
        return rows[0]?.product_id || null;
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load team overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectTeam = (productId) => {
    setSelectedId(productId);
    setSelectedMember(null);
    setDetail(null);
  };

  const openMember = async (productId, member) => {
    setSelectedMember({ productId, userId: member.user_id, name: member.name });
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(
        `${PRODUCTS_API}/manager/teams/${productId}/members/${member.user_id}`,
        authHeaders(),
      );
      setDetail(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load member detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const selectedTeam = teams.find((t) => t.product_id === selectedId) || null;

  if (loading) {
    return (
      <ModuleShell
        title="My Teams"
        icon={UsersThree}
        subtitle="Choose a product team to view its roster and active work."
      >
        <SkeletonBlock />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title="My Teams"
      icon={UsersThree}
      subtitle="Choose a product team to view its roster and active work."
    >
    <div style={{ maxWidth: '1200px' }}>

      {error && (
        <div style={{ padding: '12px', background: '#FEE2E2', color: '#B91C1C', borderRadius: '8px', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {teams.length === 0 ? (
        <EmptyState
          icon={UsersThree}
          title="No teams yet"
          description="You are not assigned as a manager on any product team yet."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <section className="pm-card" style={{ margin: 0 }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: '#6B7280',
              marginBottom: '10px',
              textTransform: 'uppercase',
            }}
            >
              Your teams ({teams.length})
            </div>
            <div className="pm-product-strip" data-testid="my-teams-product-strip">
              {teams.map((team) => {
                const active = selectedId === team.product_id;
                const memberCount = team.member_count ?? (team.members || []).length;
                const activeTickets = team.active_total
                  ?? (team.members || []).reduce((n, m) => n + (m.active_count || 0), 0);
                const openTotal = team.open_total
                  ?? (team.members || []).reduce((n, m) => n + (m.open_count || 0), 0);
                const ipTotal = team.in_progress_total
                  ?? (team.members || []).reduce((n, m) => n + (m.in_progress_count || 0), 0);
                const closedTotal = team.closed_total
                  ?? (team.members || []).reduce((n, m) => n + (m.closed_count || 0), 0);
                const managerName = team.manager?.name
                  || (team.members || []).find((m) => m.org_role === 'manager')?.name;

                return (
                  <button
                    key={team.product_id}
                    type="button"
                    onClick={() => selectTeam(team.product_id)}
                    aria-pressed={active}
                    style={{
                      flex: '0 0 auto',
                      width: '240px',
                      minHeight: '132px',
                      padding: '14px',
                      textAlign: 'left',
                      border: active ? '2px solid #002FA7' : '1px solid #E5E7EB',
                      borderRadius: '12px',
                      background: active ? '#EFF6FF' : '#FFFFFF',
                      cursor: 'pointer',
                      boxShadow: active
                        ? '0 2px 8px rgba(0, 47, 167, 0.10)'
                        : '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: active ? '#DBEAFE' : '#F3F4F6',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                        >
                          <UsersThree size={20} color={active ? '#002FA7' : '#6B7280'} weight={active ? 'fill' : 'regular'} />
                        </div>
                        <span
                          aria-label={`${memberCount} team members`}
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            minWidth: 18,
                            height: 18,
                            padding: '0 5px',
                            borderRadius: 999,
                            background: '#002FA7',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 700,
                            display: 'grid',
                            placeItems: 'center',
                            lineHeight: 1,
                            border: '2px solid #fff',
                          }}
                        >
                          {memberCount}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: '#111827',
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        >
                          {team.product_name}
                        </div>
                        {team.product_key && (
                          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: 2 }}>{team.product_key}</div>
                        )}
                        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: 6 }}>
                          {managerName ? `Manager: ${managerName}` : 'No manager'}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 6,
                      marginTop: 12,
                      paddingTop: 10,
                      borderTop: '1px solid #E5E7EB',
                    }}
                    >
                      <StatChip label="Active" value={activeTickets} emphasize={active} />
                      <StatChip label="Open" value={openTotal} />
                      <StatChip label="In prog" value={ipTotal} />
                      <StatChip label="Closed" value={closedTotal} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section style={{
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            background: '#FFFFFF',
            overflow: 'hidden',
          }}
          >
            {!selectedTeam ? (
              <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9CA3AF' }}>
                <UsersThree size={36} style={{ opacity: 0.35, marginBottom: '10px' }} />
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#6B7280' }}>
                  Select a team card to view members
                </p>
              </div>
            ) : (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  background: '#F8FAFC',
                  borderBottom: '1px solid #E5E7EB',
                  flexWrap: 'wrap',
                }}
                >
                  <div>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      color: '#002FA7',
                      textTransform: 'uppercase',
                      marginBottom: '2px',
                    }}
                    >
                      Team roster
                    </div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                      {selectedTeam.product_name}
                    </h3>
                  </div>
                  <Link
                    to={`/dashboard/tickets/pipeline?product_id=${encodeURIComponent(selectedTeam.product_id)}`}
                    className="tk-btn tk-btn-secondary"
                    style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <Package size={16} />
                    Product 360°
                    <ArrowRight size={14} />
                  </Link>
                </div>

                <div style={{ padding: '8px 12px 12px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', background: '#FAFAFA' }}>
                        <th style={thStyle}>Member</th>
                        <th style={thStyle}>Role</th>
                        <th style={thStyle}>Open</th>
                        <th style={thStyle}>In progress</th>
                        <th style={thStyle}>Active</th>
                        <th style={thStyle}>Working on</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedTeam.members || []).map((m) => (
                        <tr key={`${selectedTeam.product_id}-${m.user_id}`} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={tdStyle}>
                            <button
                              type="button"
                              onClick={() => openMember(selectedTeam.product_id, m)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: '#002FA7',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                            >
                              {m.name}
                            </button>
                            {m.designation && (
                              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: 2 }}>{m.designation}</div>
                            )}
                            {m.other_products?.length > 0 && (
                              <div style={{ fontSize: '11px', color: '#B45309', marginTop: '2px' }}>
                                Also on: {m.other_products.join(', ')}
                              </div>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              background: '#EFF6FF',
                              color: '#1D4ED8',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                            }}
                            >
                              {m.org_role}
                            </span>
                          </td>
                          <td style={tdStyle}>{m.open_count ?? 0}</td>
                          <td style={tdStyle}>{m.in_progress_count ?? 0}</td>
                          <td style={tdStyle}>{m.active_count}</td>
                          <td style={tdStyle}>
                            {(m.active_tickets || []).slice(0, 3).map((t) => (
                              <div key={t.ticket_no} style={{ marginBottom: '2px' }}>
                                <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontSize: '12px' }}>
                                  {t.ticket_no}
                                </TicketDetailLink>
                                {' '}
                                <TicketStatusBadge status={t.status} />
                              </div>
                            ))}
                            {(m.active_tickets || []).length === 0 && <span style={{ color: '#9CA3AF' }}>—</span>}
                          </td>
                        </tr>
                      ))}
                      {(selectedTeam.members || []).length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ ...tdStyle, color: '#9CA3AF' }}>No members on this team yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {selectedMember && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedMember.name} workload`}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.45)',
            zIndex: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => { setSelectedMember(null); setDetail(null); }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '720px',
              maxHeight: 'min(560px, calc(100vh - 48px))',
              display: 'flex',
              flexDirection: 'column',
              background: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              borderBottom: '1px solid #E5E7EB',
              background: '#F8FAFC',
              flexShrink: 0,
            }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>{selectedMember.name} — workload</h3>
                {detail && (
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0' }}>
                    Product: <strong>{detail.product_name}</strong> · Role: {detail.member?.org_role}
                  </p>
                )}
              </div>
              <button type="button" onClick={() => { setSelectedMember(null); setDetail(null); }} style={btnSecondary}>Close</button>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '12px 16px 16px' }}>
              {detailLoading && <p style={{ color: '#6B7280', margin: 0 }}>Loading…</p>}
              {detail && (
                detail.tickets.length === 0 ? (
                  <p style={{ color: '#6B7280', margin: 0 }}>No tickets assigned.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ textAlign: 'left', background: '#F9FAFB' }}>
                        <th style={thStyle}>Ticket</th>
                        <th style={thStyle}>Subject</th>
                        <th style={thStyle}>Client / Branch</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.tickets.map((t) => (
                        <tr key={t.ticket_no} style={{ borderBottom: '1px solid #E5E7EB' }}>
                          <td style={tdStyle}>
                            <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: '600' }}>{t.ticket_no}</TicketDetailLink>
                          </td>
                          <td style={tdStyle}>{t.subject}</td>
                          <td style={tdStyle}>{ticketClientBranchLabel(t)}</td>
                          <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                          <td style={tdStyle}>{t.priority}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

function StatChip({ label, value, emphasize }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: 13,
        fontWeight: 700,
        color: emphasize ? '#002FA7' : '#111827',
        lineHeight: 1.1,
      }}
      >
        {value}
      </div>
      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default ManagerTeamPage;
